import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import DragDropFileUpload from '../FileUpload/DragDropFileUpload';
import { convertAndSetFile } from '../Util/Util';
import { selectFiles, updateFileField } from '../../../Store/userSlice';

const DirectorDocumentSet = ({
  index,
  control,
  errors,
  onRemove,
  defaultExpanded = false,
  validationRules = {},
  setValue
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const toggleExpand = useCallback(() => setIsExpanded(prev => !prev), []);

  const dispatch = useDispatch();
  const files = useSelector(selectFiles);

  const fileToBase64 = useCallback((file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    }), []);

  const handleFileSelect = useCallback((type, index) => async (selectedFile) => {
    if (!selectedFile) return;
    
    try {
      const base64 = await fileToBase64(selectedFile);
      const fileData = { base64, filename: selectedFile.name };
      dispatch(updateFileField({ field: type, index, value: fileData }));
    } catch (error) {
      console.error('Failed to convert file to base64:', error);
    }
  }, [dispatch, fileToBase64]);

  useEffect(() => {
    const panFile = files?.panCard?.[index];
    const aadharFile = files?.AadharCard?.[index];
    
    setValue(`directors[${index}].pan`, convertAndSetFile(panFile));
    setValue(`directors[${index}].aadhar`, convertAndSetFile(aadharFile));
  }, [files?.panCard, files?.AadharCard, index, setValue]);

  return (
    <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box
        onClick={toggleExpand}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}
      >
        <Typography variant="h6">
          {index === 0
            ? 'Upload Details Documents for Director (Optional)'
            : `Upload Details Documents for Director ${index + 1} (Optional)`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {index > 0 && (
            <Button
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              size="small"
            >
              Remove
            </Button>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* PAN Upload */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
              Please upload the PAN of the director (Optional)
              </Typography>
              <Controller
                name={`directors[${index}].pan`}
                control={control}
                rules={validationRules.pan}
                render={({ field }) => (
                  <DragDropFileUpload
                    value={convertAndSetFile(files?.panCard?.[index])}
                    error={!!errors?.directors?.[index]?.pan}
                    helperText={errors?.directors?.[index]?.pan?.message}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect('panCard', index)(file);
                    }}
                  />
                )}
              />
            </Box>

            {/* Aadhar Upload */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
              Please upload the Aadhar card of the director (Optional)
              </Typography>
              <Controller
                name={`directors[${index}].aadhar`}
                control={control}
                rules={validationRules.aadhar}
                render={({ field }) => (
                  <DragDropFileUpload
                    value={convertAndSetFile(files?.AadharCard?.[index])}
                    error={!!errors?.directors?.[index]?.aadhar}
                    helperText={errors?.directors?.[index]?.aadhar?.message}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect('AadharCard', index)(file);
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DirectorDocumentSet;
